/**
 * Vertrekt er op dit moment echt mail? — en wat zeg je dan op het scherm.
 *
 * Waarom dit bestaat: twee schermen beloofden dat er mail wegging terwijl de
 * verzendvlag uit staat. Het beheerscherm zei "Tijdens de pilot gaan alleen deze soorten
 * berichten echt de deur uit: …" en de deelnemer las na "Liever later" dat de link naar
 * zijn e-mailadres ging. Geen van beide klopte: `EMAIL_SENDER_ENABLED` staat standaard
 * uit en de verzender stopt daarop, dus het bericht blijft op `pending` staan. Wie dat
 * leest, wacht op een mail die niet komt — en zoekt hem in zijn spam.
 *
 * De stand komt uit `GET /api/` (`email_sender_enabled`) en wordt hier NIET nagemaakt:
 * een tweede kopie van die vlag in de frontend gaat gegarandeerd uit de pas lopen met de
 * backend. Precies zoals `mail_toegestaan` al gaat (§4.10).
 *
 * Drie standen, geen twee. Naast "aan" en "uit" bestaat "onbekend": de aanvraag naar
 * `GET /api/` kan mislukken of nog onderweg zijn. Dan wordt er niets beweerd — liever
 * niets zeggen dan iets zeggen dat misschien niet klopt.
 *
 * Deze functies zijn zuiver en staan daarom los van de schermen, in lijn met
 * `berichtVertrektEcht` en `resendMelding` op de notificatiepagina. Twee schermen lezen
 * dezelfde stand: het beheerscherm en de gegevenspagina van de deelnemer.
 */

/**
 * De verzendstand uit het antwoord van `GET /api/`.
 *
 * @param {object|null|undefined} apiRoot  het antwoord van GET /api/, of niets
 * @returns {boolean|null}  true = er vertrekt mail, false = alles blijft staan,
 *                          null = onbekend (nog onderweg of niet op te halen)
 */
export function verzendenStaatAan(apiRoot) {
  const vlag = apiRoot?.email_sender_enabled;
  if (typeof vlag !== "boolean") return null;
  return vlag;
}

/**
 * De melding bovenaan de berichtenpagina van de beheerder (§4.10).
 *
 * @param {boolean|null} verzendenAan  uit `verzendenStaatAan`
 * @param {string[]} soortnamen        de toegestane soorten, al in gewone woorden
 */
export function pilotVerzendMelding(verzendenAan, soortnamen) {
  const lijst = (soortnamen || []).join(", ");
  if (verzendenAan === false) {
    return `Tijdens de pilot gaat er geen mail de deur uit: alles wordt klaargezet en `
      + `blijft staan. Zodra het verzenden weer aan gaat, vertrekken alleen deze soorten: `
      + `${lijst}.`;
  }
  if (verzendenAan === true) {
    return `Tijdens de pilot gaan alleen deze soorten berichten echt de deur uit: ${lijst}. `
      + `De rest wordt klaargezet en blijft staan.`;
  }
  // Onbekend: wel vertellen wat er op de lijst staat, niets beweren over vertrekken.
  return `Tijdens de pilot staan alleen deze soorten berichten op de verzendlijst: ${lijst}. `
    + `De rest wordt klaargezet en blijft staan.`;
}

/**
 * Wat de deelnemer leest ná een klik op "Liever later" (§4.6).
 *
 * @param {boolean} alVerstuurd        `al_verstuurd` uit de respons (de 24-uursontdubbeling)
 * @param {boolean|null} verzendenAan  uit `verzendenStaatAan`
 */
export function nulmetingLinkMelding(alVerstuurd, verzendenAan) {
  if (verzendenAan === false) {
    return alVerstuurd
      ? "De link staat al voor u klaar. U hoeft niets te doen — u ontvangt hem per "
        + "e-mail zodra wij de berichten weer versturen."
      : "De link is voor u klaargezet. U ontvangt hem per e-mail zodra wij de berichten "
        + "weer versturen.";
  }
  return alVerstuurd
    ? "De link is al naar u onderweg. U hoeft niets te doen."
    : "De link gaat naar uw e-mailadres. U kunt de vragenlijst later invullen.";
}

/**
 * De uitnodigende regel bóven de twee knoppen, vóór er geklikt is (§4.6).
 *
 * Staat hier omdat hij dezelfde belofte deed als de bevestiging eronder: "dan sturen wij
 * de link naar uw e-mailadres". Zou alleen de bevestiging eerlijk gemaakt worden, dan
 * spreekt het scherm zichzelf tegen binnen één kader.
 *
 * @param {boolean|null} verzendenAan  uit `verzendenStaatAan`
 */
export function laterKeuzeUitleg(verzendenAan) {
  if (verzendenAan === false) {
    return "Invullen duurt ongeveer tien minuten. Doet u het liever later, dan zetten "
      + "wij de link voor u klaar; u ontvangt hem per e-mail zodra wij de berichten weer "
      + "versturen.";
  }
  return "Invullen duurt ongeveer tien minuten. Doet u het liever later, dan sturen wij "
    + "de link naar uw e-mailadres.";
}
